// App configuration. Reuses the SAME Firebase project as the webui so the backend
// (firebase_admin, project text-to-speech-385221) can verify the anonymous ID tokens.

export const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyBFlCfyPM27SbvD7qxmjel88J8CTXlCRYI',
  authDomain: 'accounts.lean-mean-learning-machine.com',
  projectId: 'text-to-speech-385221',
  storageBucket: 'text-to-speech-385221.firebasestorage.app',
  messagingSenderId: '118139689799',
  appId: '1:118139689799:web:05cc760bec7af5cf270a62',
  measurementId: 'G-HVJZP8EHE6',
};

// Public chat API base (…/api/v1).
// - Live site (production build): the production backend (aiclerk-simple-interact).
// - Local dev (`vite`/`vite dev`): the staging backend, so local testing never hits prod.
// Override either with VITE_BACKEND_BASE at build/run time.
const PROD_BACKEND_BASE =
  'https://aiclerk-simple-interact-118139689799.europe-west1.run.app/api/v1';
const STAGING_BACKEND_BASE =
  'https://ai-desk-clerk-api-staging-118139689799.europe-west1.run.app/api/v1';

export const BACKEND_BASE =
  (import.meta.env.VITE_BACKEND_BASE as string | undefined)?.replace(/\/+$/, '') ||
  (import.meta.env.DEV ? STAGING_BACKEND_BASE : PROD_BACKEND_BASE);

// reCAPTCHA Enterprise site key for Firebase App Check (public value, NOT a secret). Wired through a
// Vite build-time env var like VITE_BACKEND_BASE above. The project owner must create the key
// in the Firebase console and supply it as VITE_RECAPTCHA_SITE_KEY at build time. If unset,
// App Check initialization is skipped entirely (see firebase.ts) so local builds still run.
export const RECAPTCHA_SITE_KEY =
  (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined)?.trim() || '';

export const CANCEL_REDIRECT_URL = 'https://lean-mean-learning-machine.com';

export const MAX_RECORDING_MS = 10000; // tap-to-talk hard cap

// --- Live STT streaming (word-by-word captions while recording) -------------
// Connect/ready cover a Cloud Run cold start as far as possible; past these the
// turn falls back to the classic whole-blob audio upload (mirrors the kiosk).
export const STT_STREAM_READY_TIMEOUT_MS = 10000;
// Max wait for the final transcript after the recording stops before falling
// back to the audio upload.
export const STT_FINAL_TIMEOUT_MS = 4000;
// Keep the finished sentence on screen for at least this long (the voice_text
// request already runs underneath, so the hold overlaps the network round-trip).
export const TRANSCRIPT_HOLD_MS = 1000;
// Absolute cap on a single response playback, so a stalled video/audio (which may never
// fire ended/error on flaky mobile networks) can never wedge the 'response' state.
export const MAX_RESPONSE_MS = 90000;

// In the wait/eager states the send button stays disabled until the typed question has at
// least this many (trimmed) characters.
export const MIN_SEND_CHARS = 2;

// Primary language subtag -> dedicated button artwork. Only these three locales ship an
// image; every other configured language renders as a text button (see LANG_INFO).
export const LANG_IMAGES: Record<string, string> = {
  nl: '/images/buttons/language_Dutch.png',
  fr: '/images/buttons/language_French.png',
  en: '/images/buttons/language_English.png',
};

// Primary subtag -> short button label + endonym (the language's own name). Anything not
// listed falls back to the uppercased subtag (label) and the raw locale (name).
export const LANG_INFO: Record<string, { label: string; name: string }> = {
  nl: { label: 'NL', name: 'Nederlands' },
  fr: { label: 'FR', name: 'Français' },
  en: { label: 'EN', name: 'English' },
  de: { label: 'DE', name: 'Deutsch' },
  es: { label: 'ES', name: 'Español' },
  it: { label: 'IT', name: 'Italiano' },
  pt: { label: 'PT', name: 'Português' },
  ro: { label: 'RO', name: 'Română' },
  pl: { label: 'PL', name: 'Polski' },
  uk: { label: 'UK', name: 'Українська' },
  ru: { label: 'RU', name: 'Русский' },
  tr: { label: 'TR', name: 'Türkçe' },
  ar: { label: 'AR', name: 'العربية' },
  cmn: { label: '中文', name: '中文' },
  zh: { label: '中文', name: '中文' },
};

// "Type your question" hardcoded per primary subtag; falls back to English.
export const INPUT_PLACEHOLDERS: Record<string, string> = {
  en: 'Type your question…',
  nl: 'Typ je vraag…',
  fr: 'Posez votre question…',
  de: 'Stellen Sie Ihre Frage…',
  es: 'Escribe tu pregunta…',
  it: 'Scrivi la tua domanda…',
  pt: 'Escreva a sua pergunta…',
  ro: 'Scrie întrebarea ta…',
  pl: 'Wpisz swoje pytanie…',
  uk: 'Введіть своє запитання…',
  ru: 'Введите ваш вопрос…',
  tr: 'Sorunuzu yazın…',
  ar: 'اكتب سؤالك…',
  cmn: '输入您的问题…',
  zh: '输入您的问题…',
};

// Shown when the recording countdown reaches 0 (and again when the visitor taps record to
// append anyway); per primary subtag, falls back to English.
export const OUT_OF_TIME_MESSAGES: Record<string, string> = {
  en: "You're out of recording time. Send your message first.",
  nl: 'Je opnametijd is op. Verstuur eerst je bericht.',
  fr: "Votre temps d'enregistrement est écoulé. Envoyez d'abord votre message.",
  de: 'Ihre Aufnahmezeit ist abgelaufen. Senden Sie zuerst Ihre Nachricht.',
  es: 'Se acabó el tiempo de grabación. Envía primero tu mensaje.',
  it: 'Il tempo di registrazione è finito. Invia prima il tuo messaggio.',
  pt: 'O tempo de gravação terminou. Envie primeiro a sua mensagem.',
  ro: 'Timpul de înregistrare s-a terminat. Trimite mai întâi mesajul.',
  pl: 'Czas nagrywania się skończył. Najpierw wyślij wiadomość.',
  uk: 'Час запису вичерпано. Спочатку надішліть повідомлення.',
  ru: 'Время записи истекло. Сначала отправьте сообщение.',
  tr: 'Kayıt süreniz doldu. Önce mesajınızı gönderin.',
  ar: 'انتهى وقت التسجيل. أرسل رسالتك أولاً.',
  cmn: '录音时间已用完，请先发送消息。',
  zh: '录音时间已用完，请先发送消息。',
};

/** Primary language subtag, lower-cased (e.g. 'nl-BE' -> 'nl', 'cmn-CN' -> 'cmn'). */
export function primarySubtag(code: string): string {
  return code.split('-')[0].toLowerCase();
}
